<template>
  <!-- action bar เดียวกับหน้า sessions: stat ซ้าย + ปุ่มขวา (title หน้าใช้ของ layout อยู่แล้ว) -->
  <div class="session-bar mb-3">
    <span class="pos-stat bg-success-subtle text-success-emphasis">
      <span class="dot bg-success"/>
      {{ total }} {{ $t('Active') }}
    </span>
    <div class="session-bar__btns">
      <button
        type="button"
        class="btn btn-sm btn-outline-secondary fw-bold rounded-3"
        :disabled="loading"
        @click="fetchData"
      >
        <i class="fa-solid fa-rotate" :class="{ 'fa-spin': loading }"/>
      </button>
      <button type="button" class="btn btn-sm btn-theme fw-bold rounded-3" @click="newOrder.open = true">
        <i class="fa-solid fa-plus me-2"/>{{ $t('_delivery.New Platform Order') }}
      </button>
    </div>
  </div>

  <div v-if="pageLoading">
    <p-o-s-page-skeleton />
  </div>
  <div v-else-if="!hasPermission">
    <p-o-s-access-denied />
  </div>
  <template v-else>
    <div v-if="!sessions.length" class="text-center text-muted py-5">
      <i class="fa-solid fa-mobile-screen-button fa-2xl mb-3 opacity-50"/>
      <p class="fw-bold mb-1">{{ $t('_delivery.No platform orders right now') }}</p>
      <p class="small mb-3">{{ $t('_delivery.Orders you key from Grab or LINE MAN will show up here until closed') }}</p>
      <button class="btn btn-theme text-white fw-bold" @click="newOrder.open = true">
        <i class="fa-solid fa-circle-plus me-2"/>{{ $t('_delivery.New Platform Order') }}
      </button>
    </div>

    <div class="row g-3">
      <div v-for="s in sessions" :key="s.id" class="col-12 col-md-6 col-xl-4">
        <div class="po-card position-relative">
          <span class="po-rail" :style="{ background: (s.delivery_platform && s.delivery_platform.color) || '#6B7280' }"/>

          <div class="po-body">
            <!-- HEAD: แพลตฟอร์ม + #เลข (ซ้าย) · ยอดเงิน / ยังไม่คีย์เมนู (ขวา) -->
            <div class="po-head">
              <div class="po-titles">
                <span class="po-num">#{{ displayOrderNo(s) }}</span>
                <span
                  v-if="s.delivery_platform"
                  class="po-platform"
                  :style="{ backgroundColor: s.delivery_platform.color || '#6B7280' }"
                >
                  <i v-if="s.delivery_platform.icon" :class="s.delivery_platform.icon" class="me-1"/>{{ s.delivery_platform.name }}
                </span>
              </div>
              <!-- 0 รายการ = ยังไม่ได้คีย์เมนู → ไม่โชว์ ฿0.00 หลอก ๆ แต่ขึ้น pill เตือนแทน -->
              <div v-if="!isEmptyOrder(s)" class="po-amount">{{ $storeIndex.formatThaiCurrency(s.total || 0) }}</div>
              <span v-else class="po-empty">
                <span class="po-empty__dot"/>{{ $t('_delivery.No items keyed yet') }}
              </span>
            </div>

            <!-- META: รับเอง · เลขอ้างอิง · รอมานานแค่ไหน · จำนวน (ข้อความจาง — เหลือ pill ไว้เฉพาะที่ต้องเตือน) -->
            <div class="po-meta">
              <span v-if="s.is_pickup" class="po-meta-item">
                <i class="fa-solid fa-person-walking"/>{{ $t('_delivery.Customer self pickup') }}
              </span>
              <span v-if="s.external_order_ref" class="po-meta-item">
                <i class="fa-solid fa-hashtag"/>{{ s.external_order_ref }}
              </span>
              <!-- เวลาเป็น elapsed pill: เหลือง ≥15 นาที / แดง ≥30 นาที (title = เวลาที่คีย์) -->
              <span class="po-elapsed" :class="elapsedClass(s)" :title="formatTime(s.created_at)">
                <i class="fa-regular fa-clock me-1"/>{{ elapsedLabel(s) }}
              </span>
              <span v-if="!isEmptyOrder(s)" class="po-meta-item">
                <i class="fa-solid fa-bowl-food"/>{{ s.items_count || 0 }} {{ $t('_delivery.items') }}
              </span>
            </div>
          </div>

          <div class="po-divider"/>

          <!-- FOOT: ปุ่มจัดการ — ยังไม่คีย์เมนู = ปุ่มหลักพาไปเริ่มคีย์ -->
          <div class="po-foot">
            <router-link
              class="btn btn-sm btn-theme text-white fw-bold rounded-3 flex-grow-1"
              :to="{ name: 'pos.sessions.detail', params: { token: s.token } }"
            >
              <template v-if="isEmptyOrder(s)">
                <i class="fa-solid fa-pen me-2"/>{{ $t('_delivery.Start keying order') }}
              </template>
              <template v-else>
                <i class="fa-solid fa-utensils me-2"/>{{ $t('_delivery.Open order') }}
              </template>
            </router-link>
            <button
              class="btn btn-sm btn-outline-secondary fw-bold rounded-3"
              :disabled="closingToken === s.token"
              @click="handleClose(s)"
            >
              <i class="fa-solid fa-spinner fa-spin" v-if="closingToken === s.token"/>
              <template v-else><i class="fa-solid fa-flag-checkered me-1"/>{{ $t('_delivery.Done') }}</template>
            </button>
            <button
              class="btn btn-sm btn-outline-danger rounded-3"
              :disabled="closingToken === s.token"
              @click="handleCancel(s)"
            >
              <i class="fa-solid fa-ban"/>
            </button>
          </div>
        </div>
      </div>
    </div>
  </template>

  <modal-new-platform-order
    v-if="newOrder.open"
    @close="newOrder.open = false"
    @saved="handleCreated"
  />
</template>

<script>
import { Sessions } from '@/services/endpoint/pos.service';
import ModalNewPlatformOrder from './ModalNewPlatformOrder.vue';
import POSPageSkeleton from '@/plugins/POSPageSkeleton.vue';
import POSAccessDenied from '@/plugins/POSAccessDenied.vue';
import moment from 'moment';

/**
 * หน้า "ออเดอร์ Platform" — ออเดอร์จาก delivery platform ภายนอกแยกขาดจากหน้า sessions
 * (sessions ปกติถูกกรอง channel=store แล้ว) — docs ฝั่ง api: delivery-platform-pricing.md §7
 * ปุ่ม "เสร็จสิ้น" = ปิด session (บิลจ่ายเป็น platform_pending ไปแล้วตั้งแต่ส่งครัว)
 */
export default {
  name: 'POSPlatformOrders',
  components: { ModalNewPlatformOrder, POSPageSkeleton, POSAccessDenied },
  data() {
    return {
      pageLoading: true,
      loading: false,
      hasPermission: false,
      sessions: [],
      total: 0,
      closingToken: null,
      newOrder: { open: false },
      now: Date.now(),
      nowTimer: null
    };
  },
  methods: {
    displayOrderNo(session) {
      return String(session.session_no || 0).padStart(4, '0');
    },
    formatTime(value) {
      return value ? moment(value).format('HH:mm') : '';
    },
    isEmptyOrder(session) {
      return !(session.items_count > 0);
    },
    // อ้าง this.now เพื่อให้ label เดินหน้าเองตาม timer (getTimeDiff คิดจากเวลาปัจจุบันภายใน)
    elapsedLabel(session) {
      void this.now;
      return session.created_at ? this.$storeIndex.getTimeDiff(session.created_at) : '-';
    },
    elapsedMinutes(session) {
      if (!session.created_at) return 0;
      return Math.floor((this.now - new Date(session.created_at).getTime()) / 60000);
    },
    elapsedClass(session) {
      const m = this.elapsedMinutes(session);
      if (m >= 30) return 'po-elapsed--late';
      if (m >= 15) return 'po-elapsed--warn';
      return '';
    },
    async fetchData() {
      this.loading = true;
      const queryString = '?' + $.param({ channel: 'platform', limit: 100, page: 1 });
      await Sessions.list(queryString).then(res => {
        this.sessions = res.data?.data ?? [];
        this.total = res.data?.meta?.total ?? this.sessions.length;
        this.hasPermission = true;
      }).catch(err => {
        if (err?.response?.status === 403) this.hasPermission = false;
        else if (err?.response?.data?.message) this.$toast.warn(this.$t(err?.response?.data?.message));
      }).finally(() => {
        this.loading = false;
        this.pageLoading = false;
      });
    },
    handleCreated({ token }) {
      this.newOrder.open = false;
      // เปิด session สำเร็จ → เข้าหน้าคีย์เมนูทันที
      if (token) this.$router.push({ name: 'pos.sessions.detail', params: { token } });
    },
    /**
     * platform ยกเลิกออเดอร์ (เงินไม่มา) — 3 ทาง: คืนสต็อก / ไม่คืน (ทำอาหารแล้ว=waste) / ปิดกล่อง
     * เงินสดและตัวเลขกะไม่ถูกแตะ — void platform_pending + บิล refunded + ออเดอร์ cancelled
     */
    handleCancel(session) {
      const self = this;
      this.$swal({
        icon: 'warning',
        iconColor: '#FF7043',
        title: this.$t('_delivery.Cancel platform order') + ' #' + this.displayOrderNo(session) + '?',
        html: this.$t('_delivery.Use when the platform cancelled and will not pay - restock if the food was not made yet'),
        showDenyButton: true,
        showCancelButton: true,
        cancelButtonText: this.$t('Cancel'),
        confirmButtonText: '<i class="fas fa-rotate-left me-1"></i>' + this.$t('_delivery.Cancel and restock'),
        denyButtonText: '<i class="fas fa-trash-can me-1"></i>' + this.$t('_delivery.Cancel without restock'),
        buttonsStyling: false,
        customClass: { confirmButton: 'btn btn-primary me-2', denyButton: 'btn btn-warning me-2', cancelButton: 'btn btn-light border' },
        focusConfirm: false,
        showLoaderOnConfirm: true,
        preConfirm: () => self.doCancel(session, true),
        preDeny: () => self.doCancel(session, false)
      });
    },
    async doCancel(session, isRestock) {
      this.closingToken = session.token;
      return Sessions.platformCancel(session.token, isRestock)
        .then(() => {
          this.$toast.success('<b>' + this.$t('Success') + '</b> ' + this.$t('_delivery.Order cancelled'));
          this.fetchData();
        })
        .catch((error) => {
          if (error?.response?.data?.message) {
            this.$swal.showValidationMessage(error.response.data.message);
          }
        })
        .finally(() => {
          this.closingToken = null;
        });
    },
    handleClose(session) {
      const self = this;
      this.$swal({
        icon: 'question',
        iconColor: '#FF7043',
        title: this.$t('_delivery.Finish this platform order') + ' #' + this.displayOrderNo(session) + '?',
        html: this.$t('_delivery.Close when the rider or customer has picked up the food'),
        showCancelButton: true,
        cancelButtonText: this.$t('Cancel'),
        confirmButtonText: '<i class="fas fa-check-circle me-1"></i>' + this.$t('Confirm'),
        buttonsStyling: false,
        customClass: { confirmButton: 'btn btn-primary me-2', cancelButton: 'btn btn-danger' },
        focusConfirm: false,
        showLoaderOnConfirm: true,
        preConfirm: async function () {
          self.closingToken = session.token;
          return Sessions.close(session.token)
            .then(() => {
              self.$toast.success('<b>' + self.$t('Success') + '</b> ' + self.$t('_delivery.Order closed'));
              self.fetchData();
            })
            .catch((error) => {
              if (error?.response?.data?.message) {
                self.$swal.showValidationMessage(error.response.data.message);
              }
            })
            .finally(() => {
              self.closingToken = null;
            });
        }
      });
    }
  },
  mounted() {
    this.fetchData();
    // เดิน elapsed pill ทุก 30 วิ ให้สีเตือนอัปเดตเองโดยไม่ต้องรีเฟรช
    this.nowTimer = setInterval(() => { this.now = Date.now(); }, 30000);
  },
  beforeUnmount() {
    if (this.nowTimer) clearInterval(this.nowTimer);
  }
};
</script>

<style scoped>
/* R2-style platform order card — rail สีแพลตฟอร์ม + หัว (ยอดเงินขวา) + เส้นคั่น + ปุ่ม */
.po-card {
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #fff;
  border: 1px solid #ece8e2;
  border-radius: 15px;
  box-shadow: 0 1px 2px rgba(30, 25, 20, .05), 0 3px 10px rgba(30, 25, 20, .05);
  padding: 14px 16px 14px 19px;
  transition: box-shadow .18s ease, transform .12s ease;
  overflow: hidden; /* ให้ rail โค้งตามมุมการ์ด */
}
@media (min-width: 768px) {
  .po-card:hover {
    box-shadow: 0 1px 2px rgba(30, 25, 20, .05), 0 8px 20px rgba(30, 25, 20, .10);
    transform: translateY(-2px);
  }
}

.po-rail { position: absolute; left: 0; top: 0; bottom: 0; width: 5px; }

.po-body { flex: 1 1 auto; }

.po-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 10px; }
.po-titles { display: flex; align-items: center; flex-wrap: wrap; gap: 6px 8px; min-width: 0; }
.po-platform {
  display: inline-flex; align-items: center;
  font-size: .72rem; font-weight: 700; color: #fff;
  padding: 3px 9px; border-radius: 999px; white-space: nowrap;
}
.po-num {
  font-size: 1.3rem; font-weight: 800; line-height: 1; letter-spacing: -.02em;
  color: #22242a; font-variant-numeric: tabular-nums;
}
.po-amount {
  flex: 0 0 auto;
  font-size: 1.5rem; font-weight: 800; line-height: 1; letter-spacing: -.02em;
  color: #22242a; font-variant-numeric: tabular-nums;
}

/* ยังไม่ได้คีย์เมนู — pill เตือนแทนยอดเงิน (จุดกระพริบใช้ keyframes global qrReadyPulse) */
.po-empty {
  flex: 0 0 auto;
  display: inline-flex; align-items: center; gap: 6px;
  font-size: .78rem; font-weight: 700;
  color: #8a5a00; background: #fbf1d8; border: 1px solid rgba(176, 111, 0, .25);
  padding: 4px 11px; border-radius: 999px; white-space: nowrap;
}
.po-empty__dot {
  width: 6px; height: 6px; border-radius: 50%;
  background: #d09400;
  animation: qrReadyPulse 1.8s ease-out infinite;
}

.po-meta { display: flex; flex-wrap: wrap; align-items: center; gap: 6px 14px; margin-top: 10px; }
.po-meta-item { display: inline-flex; align-items: center; gap: 6px; min-width: 0; font-size: .83rem; color: #676d76; }
.po-meta-item i { width: 13px; text-align: center; color: #a7acb3; font-size: .9em; }

/* elapsed — ปกติเป็นข้อความจางกลืนกับ meta อื่น; เป็น pill สีเฉพาะตอนรอนาน
   (≥15 นาที เหลือง / ≥30 นาที แดง) → การ์ดไม่รก แต่ของด่วนยังเด้งสายตา */
.po-elapsed {
  display: inline-flex; align-items: center;
  font-size: .83rem; font-weight: 600; color: #676d76;
  white-space: nowrap; font-variant-numeric: tabular-nums;
}
.po-elapsed i { color: #a7acb3; }
.po-elapsed--warn,
.po-elapsed--late {
  font-size: .75rem;
  font-weight: 700;
  padding: 3px 9px;
  border-radius: 999px;
}
.po-elapsed--warn { color: #8a5a00; background: #fbf1d8; }
.po-elapsed--warn i { color: #b08a3e; }
.po-elapsed--late { color: #c0303c; background: rgba(214, 69, 80, .12); animation: alert-pulse 1.3s ease-in-out infinite; }
.po-elapsed--late i { color: #c0303c; }

.po-divider { height: 1px; background: #e0dbd3; margin: 13px 0 11px; }

.po-foot { flex: 0 0 auto; display: flex; gap: 8px; }
.po-foot .btn { white-space: nowrap; }
</style>
